/**
 * Окно 3D-предпросмотра объекта каталога — «глазик» на карточке каталога чертежа,
 * 3D-каталога и вариантов модели.
 *
 * Окно рисуется порталом прямо в `body`: открывают его из панелей со своим слоем
 * (`z-10`), и внутри них оно оказалось бы под плашкой демо-режима и шапкой. Сама 3D-сцена
 * грузится лениво — three.js не попадает в основной бандл; пока грузится, в окне надпись.
 * Закрывается крестиком, кликом мимо окна и Esc.
 */
import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { View3D } from "@/shared/catalog";

const ModelPreviewScene = lazy(() => import("./ModelPreviewScene"));

interface Props {
	view3d: View3D;
	onClose: () => void;
}

export const ModelPreview = ({ view3d, onClose }: Props) => {
	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [onClose]);

	return createPortal(
		<PreviewShell onClose={onClose}>
			<Suspense
				fallback={
					<div className="flex h-full items-center justify-center text-xs text-ink">
						Загрузка 3D…
					</div>
				}
			>
				<ModelPreviewScene view3d={view3d} />
			</Suspense>
		</PreviewShell>,
		document.body,
	);
};

/** Затемнение и окно с крестиком — поверх всего интерфейса. */
function PreviewShell({ children, onClose }: { children: ReactNode; onClose: () => void }) {
	return (
		<>
			<div
				className="fixed inset-0 z-100 bg-overlay-backdrop backdrop-blur-sm"
				onClick={onClose}
				aria-hidden="true"
			/>
			<div
				role="dialog"
				aria-label="3D-предпросмотр"
				className="fixed top-1/2 left-1/2 z-101 aspect-square w-112 max-w-viewport-gutter -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-surface-raised shadow-2xl"
			>
				<button
					type="button"
					className="absolute top-2.5 right-2.5 z-10 flex size-7 items-center justify-center rounded-full bg-surface-raised text-ink shadow-md ring-1 ring-border backdrop-blur-sm transition-colors hover:text-ink"
					onClick={onClose}
					aria-label="Закрыть"
				>
					✕
				</button>
				{children}
			</div>
		</>
	);
}
